// src/auth/AdminProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './SupabaseAuthProvider';
import { checkAdminRole } from '../admin/AdminRoleManagement';

/**
 * A route component that only allows access to users with admin role
 * This component should be used for routes that require admin privileges
 */
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const verifyAdmin = async () => {
      if (!isAuthenticated || !user) {
        setIsChecking(false);
        return;
      }

      try {
        // Check if user has admin role
        const result = await checkAdminRole(user.id);
        setIsAdmin(result.success && result.isAdmin);
      } catch (error) {
        console.error('Error checking admin role:', error);
      } finally {
        setIsChecking(false);
      }
    };

    if (!isLoading) {
      verifyAdmin();
    }
  }, [isAuthenticated, isLoading, user]);

  // Show loading state while checking authentication and admin status
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to unauthorized page if not an admin
  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If authenticated and admin, render the children
  return children;
};

export default AdminProtectedRoute;